/**
 * Achievement and DLC Editing Dialogs for Master Database
 * - Edit Achievement Dialog
 * - Edit DLC Dialog
 * - Bulk Edit Achievement Dialog
 */
import { Loader2, Save, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { buildTagSections } from "./groupedTags";

// Helper to format YYYY-MM-DD to MM/DD/YYYY for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return dateStr;
};

// Helper for auto-masking date input (MM/DD/YYYY)
const handleDateInputMask = (inputValue) => {
  let digits = inputValue.replace(/\D/g, "");
  digits = digits.slice(0, 8);
  
  let formatted = "";
  if (digits.length > 0) {
    formatted = digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += "/" + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += "/" + digits.slice(4, 8);
  }
  
  return formatted;
};

// Region options (fallback when no admin-managed list is provided via props)
const DEFAULT_REGION_OPTIONS = [
  { value: "none", label: "No Region" },
  { value: "WW", label: "Worldwide (WW)" },
  { value: "NA", label: "North America (NA)" },
  { value: "EU", label: "Europe (EU)" },
  { value: "JP", label: "Japan (JP)" },
  { value: "AS", label: "Asia (AS)" },
  { value: "AU", label: "Australia (AU)" },
  { value: "KR", label: "Korea (KR)" },
  { value: "CN", label: "China (CN)" },
];

/**
 * Edit Achievement Dialog
 */
export function EditAchievementDialog({
  open,
  onOpenChange,
  editingAchievement,
  onEditingAchievementChange,
  onSave,
  selectedGame,
  predefinedTags,
  tagGroups = [],
}) {
  if (!editingAchievement) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Achievement</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2 -mr-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input
              value={editingAchievement.name}
              onChange={(e) => onEditingAchievementChange({...editingAchievement, name: e.target.value})}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={editingAchievement.description || ""}
              onChange={(e) => onEditingAchievementChange({...editingAchievement, description: e.target.value})}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Score (GS)</Label>
              <Input
                type="number"
                value={editingAchievement.score}
                onChange={(e) => onEditingAchievementChange({...editingAchievement, score: parseInt(e.target.value) || 0})}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Trophy Type</Label>
              <Select 
                value={editingAchievement.trophyType || "none"} 
                onValueChange={(v) => onEditingAchievementChange({...editingAchievement, trophyType: v === "none" ? null : v})}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Achievement Image</Label>
            <div className="flex items-start gap-3">
              <ImageUpload
                folder="gamepop/achievements"
                currentImageUrl={editingAchievement.imageUrl || ""}
                onImageUploaded={(url) => onEditingAchievementChange({...editingAchievement, imageUrl: url})}
                size="sm"
              />
              <Input
                value={editingAchievement.imageUrl || ""}
                onChange={(e) => onEditingAchievementChange({...editingAchievement, imageUrl: e.target.value})}
                placeholder="Or paste URL..."
                className="bg-black/50 border-white/10 flex-1"
              />
            </div>
          </div>

          {/* Additional images — up to 4 extras. When any are present, the
              achievement image will rotate through them across the app every
              15 seconds. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                Additional Images
                <span className="ml-2 text-[10px] text-white/40 font-normal">
                  ({(editingAchievement.additionalImages || []).length}/4) — enables rotating display
                </span>
              </Label>
              {(editingAchievement.additionalImages || []).length < 4 && (
                <button
                  type="button"
                  onClick={() => onEditingAchievementChange({
                    ...editingAchievement,
                    additionalImages: [...(editingAchievement.additionalImages || []), ""],
                  })}
                  className="text-[10px] px-2 py-1 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-400/30"
                  data-testid="add-additional-image-btn"
                >
                  + Add slot
                </button>
              )}
            </div>
            {(editingAchievement.additionalImages || []).map((imgUrl, idx) => (
              <div key={idx} className="flex items-start gap-3" data-testid={`additional-image-row-${idx}`}>
                <ImageUpload
                  folder="gamepop/achievements"
                  currentImageUrl={imgUrl || ""}
                  onImageUploaded={(url) => {
                    const next = [...(editingAchievement.additionalImages || [])];
                    next[idx] = url;
                    onEditingAchievementChange({ ...editingAchievement, additionalImages: next });
                  }}
                  size="sm"
                />
                <Input
                  value={imgUrl || ""}
                  onChange={(e) => {
                    const next = [...(editingAchievement.additionalImages || [])];
                    next[idx] = e.target.value;
                    onEditingAchievementChange({ ...editingAchievement, additionalImages: next });
                  }}
                  placeholder={`Extra image ${idx + 2} URL...`}
                  className="bg-black/50 border-white/10 flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = (editingAchievement.additionalImages || []).filter((_, i) => i !== idx);
                    onEditingAchievementChange({ ...editingAchievement, additionalImages: next });
                  }}
                  className="text-red-400 hover:text-red-300 px-2 py-1 text-xs"
                  data-testid={`remove-additional-image-${idx}`}
                  title="Remove this image"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label>DLC</Label>
            <Select 
              value={editingAchievement.dlcId || "none"} 
              onValueChange={(v) => onEditingAchievementChange({...editingAchievement, dlcId: v === "none" ? null : v})}
            >
              <SelectTrigger className="bg-black/50 border-white/10">
                <SelectValue placeholder="Base Game" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="none">Base Game</SelectItem>
                {selectedGame?.dlcs?.map((dlc) => (
                  <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Achievement Tags */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-purple-400" />
              Achievement Tags
            </Label>
            <div className="flex flex-col gap-3 p-2 bg-black/30 rounded border border-white/10 max-h-60 overflow-y-auto">
              {buildTagSections(predefinedTags, tagGroups).map(section => (
                <div key={section.id} data-testid={`ach-tag-group-${section.id}`}>
                  {tagGroups.length > 0 && (
                    <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-1 px-1">
                      {section.name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {section.tags.map(tag => (
                      <label
                        key={tag.id}
                        className="flex items-center gap-1 px-2 py-1 rounded cursor-pointer hover:bg-white/10 transition-colors"
                        style={{ backgroundColor: (editingAchievement.tags || []).includes(tag.id) ? tag.color + "30" : "transparent" }}
                      >
                        <Checkbox
                          checked={(editingAchievement.tags || []).includes(tag.id)}
                          onCheckedChange={(checked) => {
                            const currentTags = editingAchievement.tags || [];
                            if (checked) {
                              onEditingAchievementChange({...editingAchievement, tags: [...currentTags, tag.id]});
                            } else {
                              onEditingAchievementChange({...editingAchievement, tags: currentTags.filter(t => t !== tag.id)});
                            }
                          }}
                        />
                        <span className="text-xs" style={{ color: tag.color }}>
                          {tag.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {(editingAchievement.tags || []).length > 0 && (
              <p className="text-xs text-purple-400">
                {editingAchievement.tags.length} tag(s) selected
              </p>
            )}
          </div>

          {/* Achievement flags (MDB-canonical). isSecret hides name/desc/image
              until unlocked. isPerCharacter enables per-character unlocks on
              the user side — see per-character marked achievement UI. */}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-start gap-2 px-2 py-2 rounded bg-black/30 border border-white/10 cursor-pointer hover:border-amber-400/30">
              <input
                type="checkbox"
                checked={!!editingAchievement.isSecret}
                onChange={(e) => onEditingAchievementChange({ ...editingAchievement, isSecret: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-amber-400"
                data-testid="mdb-ach-secret"
              />
              <div className="text-xs">
                <div className="text-white/90 font-medium">Secret</div>
                <div className="text-white/40">Hide details until unlocked</div>
              </div>
            </label>
            <label className="flex items-start gap-2 px-2 py-2 rounded bg-black/30 border border-white/10 cursor-pointer hover:border-pink-400/30">
              <input
                type="checkbox"
                checked={!!editingAchievement.isPerCharacter}
                onChange={(e) => onEditingAchievementChange({ ...editingAchievement, isPerCharacter: e.target.checked })}
                className="mt-0.5 w-4 h-4"
                style={{ accentColor: "#e11d74" }}
                data-testid="mdb-ach-per-character"
              />
              <div className="text-xs">
                <div className="text-white/90 font-medium">Per Character</div>
                <div className="text-white/40">Users can log unlocks per character</div>
              </div>
            </label>
          </div>

          <Button onClick={onSave} className="w-full bg-purple-600 hover:bg-purple-500">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Edit DLC Dialog
 */
export function EditDlcDialog({
  open,
  onOpenChange,
  editingDlc,
  onEditingDlcChange,
  onSave,
  // Admin-managed region options (falls back to built-in list)
  regionOptions = DEFAULT_REGION_OPTIONS,
}) {
  if (!editingDlc) return null;

  const handleDateChange = (e) => {
    const formatted = handleDateInputMask(e.target.value);
    if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = formatted.split('/');
      onEditingDlcChange({...editingDlc, releaseDate: `${year}-${month}-${day}`, _dateDisplay: formatted});
    } else {
      onEditingDlcChange({...editingDlc, releaseDate: "", _dateDisplay: formatted});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Edit DLC</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>DLC Name *</Label>
            <Input
              value={editingDlc.name}
              onChange={(e) => onEditingDlcChange({...editingDlc, name: e.target.value})}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Release Date</Label>
            <Input
              type="text"
              value={editingDlc._dateDisplay !== undefined ? editingDlc._dateDisplay : (editingDlc.releaseDate ? formatDateForDisplay(editingDlc.releaseDate) : "")}
              onChange={handleDateChange}
              placeholder="MM/DD/YYYY"
              className="bg-black/50 border-white/10"
              maxLength={10}
            />
          </div>
          <div className="space-y-2">
            <Label>Box Art</Label>
            <div className="flex items-start gap-3">
              <ImageUpload
                folder="gamepop/dlcs"
                currentImageUrl={editingDlc.boxArtUrl || ""}
                onImageUploaded={(url) => onEditingDlcChange({...editingDlc, boxArtUrl: url})}
                size="sm"
              />
              <Input
                value={editingDlc.boxArtUrl || ""}
                onChange={(e) => onEditingDlcChange({...editingDlc, boxArtUrl: e.target.value})}
                placeholder="Or paste URL..."
                className="bg-black/50 border-white/10 flex-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Completion Estimate</Label>
              <Input
                value={editingDlc.completionEstimate || ""}
                onChange={(e) => onEditingDlcChange({...editingDlc, completionEstimate: e.target.value})}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={editingDlc.region || "none"} onValueChange={(v) => onEditingDlcChange({...editingDlc, region: v})}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {regionOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={onSave} className="w-full bg-blue-600 hover:bg-blue-500">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Bulk Edit Achievement Dialog
 */
export function BulkEditAchievementDialog({
  open,
  onOpenChange,
  currentAchievement,
  onCurrentAchievementChange,
  currentIndex,
  totalCount,
  selectedGame,
  onSkip,
  onSave,
}) {
  if (!currentAchievement) return null;

  const handleDlcChange = (val) => {
    if (val === "none") {
      onCurrentAchievementChange({...currentAchievement, dlcId: null, dlcName: null});
    } else {
      const dlc = selectedGame?.dlcs?.find(d => d.id === val || d.name === val);
      onCurrentAchievementChange({...currentAchievement, dlcId: dlc?.id || null, dlcName: dlc?.name || val});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Achievement ({currentIndex + 1} of {totalCount})</DialogTitle>
          <DialogDescription className="text-white/60">
            {currentAchievement?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-3">
            {currentAchievement.imageUrl && (
              <img 
                src={currentAchievement.imageUrl} 
                alt="" 
                className="w-12 h-12 rounded object-cover bg-white/10"
              />
            )}
            <div className="flex-1">
              <Input
                value={currentAchievement.name || ""}
                onChange={(e) => onCurrentAchievementChange({...currentAchievement, name: e.target.value})}
                placeholder="Achievement name"
                className="bg-black/50 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={currentAchievement.description || ""}
              onChange={(e) => onCurrentAchievementChange({...currentAchievement, description: e.target.value})}
              placeholder="Achievement description"
              className="bg-black/50 border-white/10 min-h-[60px]"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Score</Label>
              <Input
                type="number"
                value={currentAchievement.score || ""}
                onChange={(e) => onCurrentAchievementChange({...currentAchievement, score: parseInt(e.target.value) || 0})}
                placeholder="Gamerscore"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>DLC</Label>
              <Select 
                value={currentAchievement.dlcId || currentAchievement.dlcName || "none"} 
                onValueChange={handleDlcChange}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select DLC" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">Base Game</SelectItem>
                  {selectedGame?.dlcs?.map((dlc) => (
                    <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={currentAchievement.isHidden || false}
              onCheckedChange={(checked) => onCurrentAchievementChange({...currentAchievement, isHidden: checked})}
            />
            <Label className="text-sm">Hidden achievement</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onSkip}
              className="flex-1 border-white/20"
            >
              Skip
            </Button>
            <Button
              onClick={onSave}
              className="flex-1 bg-purple-600 hover:bg-purple-500"
            >
              {currentIndex < totalCount - 1 ? "Save & Next" : "Save & Finish"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
